import React, { useEffect, useState } from 'react';
import { iconPaths } from '../../assets';
import type { IconName } from '../../types';

export interface IconProps {
  name: IconName;
  size?: number | string;
  color?: string;
  className?: string;
  alt?: string;
  loading?: 'lazy' | 'eager';
}

export const Icon: React.FC<IconProps> = ({
  name,
  size = 20,
  color,
  className = '',
  alt,
  loading = 'lazy',
}) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSvg = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(iconPaths[name]);
        if (!response.ok) {
          throw new Error(`Failed to load icon: ${name}`);
        }
        const text = await response.text();

        let processedSvg = text;

        if (color) {
          processedSvg = text
            .replace(/fill="#[^"]*"/g, `fill="${color}"`)
            .replace(/fill='#[^']*'/g, `fill="${color}"`)
            .replace(/stroke='#[^']*'/g, `stroke="${color}"`)
            .replace(/fill="currentColor"/g, `fill="${color}"`)
            .replace(/fill='currentColor'/g, `fill="${color}"`)
            .replace(/stroke='currentColor'/g, `stroke="${color}"`);
        } else {
          processedSvg = text
            .replace(/fill="#[^"]*"/g, 'fill="currentColor"')
            .replace(/fill='#[^']*'/g, 'fill="currentColor"')
            .replace(/stroke="#[^"]*"/g, 'stroke="currentColor"');
        }

        setSvgContent(processedSvg);
      } catch (error) {
        console.error(`Failed to load icon: ${name}`, error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSvg();
  }, [name, color]);

  if (isLoading || !svgContent) {
    return (
      <span
        className={`inline-flex items-center justify-center ${className}`}
        style={{
          width: typeof size === 'number' ? `${size}px` : size,
          height: typeof size === 'number' ? `${size}px` : size,
        }}
        aria-label={alt || name}
        role="img"
      />
    );
  }

  const sizeValue = typeof size === 'number' ? `${size}px` : size;
  const finalColor = color || 'currentColor';

  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      style={{
        width: sizeValue,
        height: sizeValue,
        color: finalColor === 'currentColor' ? undefined : finalColor,
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
      aria-label={alt || name}
      role="img"
    />
  );
};
